import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { MarginOrderDirection } from '@swyftx/margin/build/types';

import { useIntercom } from 'react-use-intercom';
import { useSwyftxMarginStore } from 'src/lib/margin';
import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { useMarginTradePanelStore } from 'src/lib/margin/hooks/trade';

import { MarginAccountDetailsCompact } from './components/Account';
import { SwyftxMarginDisclaimer } from './components/General';
import { SwyftxMarginStalePriceBanner } from './components/General/SwyftxMarginStalePriceBanner';
import {
  MarginInstrumentPanel,
  MarginTradeChart,
  MarginTradePanel,
  MarginTradePanelModal,
  MarginPositionsCard,
  MarginPositionsShowFilter,
} from './components/Trade';

const SwyftxMarginTrade: React.FC = () => {
  const { asset } = useParams();
  const { selectedMarket } = useMarginInstruments();
  const { tradeCurrency, setTradeCurrency } = useSwyftxMarginStore();
  const { reset } = useMarginTradePanelStore();
  const { update } = useIntercom();
  const { t } = useTranslation('margin');

  const updateTradeCurrency = useCallback(
    (newTradeCurrency: string) => {
      if (newTradeCurrency === tradeCurrency) return;
      setTradeCurrency(newTradeCurrency);
      reset();
    },
    [reset, setTradeCurrency, tradeCurrency],
  );

  useEffect(() => {
    if (asset) {
      updateTradeCurrency(asset);
    }
  }, [asset, updateTradeCurrency]);

  useEffect(() => {
    update({
      hideDefaultLauncher: true,
    });
  }, [update]);

  return (
    <FlexLayout
      direction='column'
      className='w-full gap-16 overflow-y-auto md:gap-8 md:p-8'
      spacing={8}
      justifyContent='start'
    >
      <SwyftxMarginStalePriceBanner containerClassName='w-full px-16 pt-16 md:p-0' />

      <MarginAccountDetailsCompact className='flex md:hidden' />
      <MarginInstrumentPanel />

      <FlexLayout
        direction='column'
        spacing={16}
        className='w-full gap-16 px-16 md:gap-8 md:p-0'
        justifyContent='space-between'
        alignItems='start'
      >
        <FlexLayout direction='row' spacing={8} className='h-[50vh] w-full md:h-[75vh] md:min-h-[700px]'>
          <MarginTradeChart />

          <div className='hidden h-full w-full md:block md:max-w-[380px]'>
            <MarginTradePanel />
          </div>
        </FlexLayout>

        <div className='w-full sm:min-h-[400px] md:max-h-[564px] md:min-h-[564px] '>
          <MarginPositionsCard groupBy='instrumentSide' actions={<MarginPositionsShowFilter />} />
        </div>

        <SwyftxMarginDisclaimer className='w-full px-16 pb-96 @container sm:px-16 md:px-32 md:py-16 lg:px-32 xl:px-56 xl:py-24' />
      </FlexLayout>

      <FlexLayout
        className='absolute bottom-0 z-10 w-full border-t border-color-border-main bg-color-background-surface-primary p-16 md:hidden'
        spacing={16}
      >
        <MarginTradePanelModal initialTradeSide={MarginOrderDirection.Long}>
          <Button variant='filled' size='lg' className='w-full' color='success' disabled={!selectedMarket?.long}>
            {t(`tradeSide.${MarginOrderDirection.Long}`)}
          </Button>
        </MarginTradePanelModal>
        <MarginTradePanelModal initialTradeSide={MarginOrderDirection.Short}>
          <Button variant='filled' size='lg' className='w-full' color='destructive' disabled={!selectedMarket?.short}>
            {t(`tradeSide.${MarginOrderDirection.Short}`)}
          </Button>
        </MarginTradePanelModal>
      </FlexLayout>
    </FlexLayout>
  );
};

export { SwyftxMarginTrade };
